"use client";

import { useState, useEffect } from "react";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { Id } from "@convex/_generated/dataModel";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { format, parseISO } from "date-fns";
import { PhotoSelectionSheet } from "@/components/ui/photo-selection-sheet";
import { isNativePlatform } from "@/lib/issue-photo";

interface QuickAddPhotoSheetProps {
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

// Get current local date/time for active block query
function getLocalDateTime() {
  const now = new Date();
  const localDate = now.toLocaleDateString("en-CA"); // YYYY-MM-DD format
  const localTime = now.toTimeString().slice(0, 5); // HH:MM format
  return { localDate, localTime };
}

export function QuickAddPhotoSheet({
  trigger,
  open,
  onOpenChange,
}: QuickAddPhotoSheetProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open !== undefined ? open : internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;

  const [selectedBlockId, setSelectedBlockId] = useState<Id<"blocks"> | null>(null);
  const [photoSheetOpen, setPhotoSheetOpen] = useState(false);

  const { localDate, localTime } = getLocalDateTime();
  const activeBlock = useQuery(api.blocks.getActiveBlock, { localDate, localTime });
  const blocks = useQuery(api.blocks.listBlocks) ?? [];
  const recentBlocks = blocks.slice(0, 7); // Show last 7 blocks

  // Only render content on native platform
  const isNative = isNativePlatform();

  // If there's an active block and sheet opens, go directly to photo selection
  useEffect(() => {
    if (isOpen && activeBlock) {
      setSelectedBlockId(activeBlock._id);
      setIsOpen(false);
      setTimeout(() => setPhotoSheetOpen(true), 100);
    }
  }, [isOpen, activeBlock, setIsOpen]);

  const handleBlockSelect = (blockId: Id<"blocks">) => {
    setSelectedBlockId(blockId);
    setIsOpen(false);
    // Small delay to let the first sheet close
    setTimeout(() => setPhotoSheetOpen(true), 100);
  };

  const formatBlockLabel = (block: { date: string; shiftStart: string; shiftEnd: string; station?: string }) => {
    const date = parseISO(block.date);
    const dateStr = format(date, "EEE, MMM d");
    const timeStr = `${block.shiftStart} - ${block.shiftEnd}`;
    return { dateStr, timeStr, station: block.station };
  };

  // Return null for trigger if not native (menu item won't show)
  if (!isNative && !trigger) {
    return null;
  }

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
        <SheetContent side="bottom" className="h-auto max-h-[70vh]">
          <SheetHeader>
            <SheetTitle>Add Photo to Block</SheetTitle>
          </SheetHeader>
          <div className="space-y-2 mt-4 overflow-y-auto">
            {recentBlocks.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No blocks found. Add a block first.
              </p>
            ) : (
              recentBlocks.map((block) => {
                const { dateStr, timeStr, station } = formatBlockLabel(block);
                return (
                  <Button
                    key={block._id}
                    variant="outline"
                    className="w-full h-auto py-3 justify-start text-left"
                    onClick={() => handleBlockSelect(block._id)}
                  >
                    <div className="flex flex-col gap-0.5">
                      <span className="font-medium">{dateStr}</span>
                      <span className="text-sm text-muted-foreground">
                        {timeStr}
                        {station && ` · ${station}`}
                      </span>
                    </div>
                  </Button>
                );
              })
            )}
          </div>
          <div className="mt-4 pb-safe">
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {selectedBlockId && (
        <PhotoSelectionSheet
          blockId={selectedBlockId}
          open={photoSheetOpen}
          onOpenChange={(open) => {
            setPhotoSheetOpen(open);
            if (!open) setSelectedBlockId(null);
          }}
          onPhotoAdded={() => {
            setPhotoSheetOpen(false);
            setSelectedBlockId(null);
          }}
        />
      )}
    </>
  );
}
